/**
 * Pure data transformation utilities for PanchangaSection
 *
 * Contains all business logic extracted from PanchangaSection:
 * - Tithi formatting
 * - Field creation helpers
 * - Divisional section factory
 * - Plugin data generation (panchanga + solar/time + divisional sections)
 */

import { JatakaResponse } from '../../../../models/api';
import { AccordionItem } from './types';
import { DIVISIONAL_CHARTS, DivisionalChart } from '../../../../config/divisionalCharts';
import { SharedChartData, SharedViewMode } from '../../../../types/share.types';

// Field configuration interface
export interface FieldConfig {
    key: string;
    label: string;
    getValue: (basicInfo: any) => string | number | undefined;
    required: boolean;
    editable?: boolean;
}

/**
 * Extract basic panchanga data from API response
 */
export function extractPanchangaData(response: JatakaResponse): { panchanga: any; detailed_panchanga: any; basic_info: any; astronomical: any } | null {
    return {
        panchanga: response.panchanga,
        detailed_panchanga: response.detailed_panchanga,
        basic_info: response.basic_info,
        astronomical: response.astronomical
    };
}

/**
 * Enhanced tithi formatting that works with both detailed and basic data
 * Preserves all original information while adding paksha and numbering
 */
export const formatTithi = (basicTithi: string, detailedTithiData?: any): string => {
    // Parse basic tithi to extract ruling planet for enhanced display
    const basicMatch = basicTithi.match(/^(.+?)\s*\((.+?)\)(?:\s*-\s*(.+?))?\s*\((.+?)%\s*left\)/);
    let rulingPlanet = '';

    if (basicMatch) {
        rulingPlanet = basicMatch[2].trim();
    }

    // If we have detailed data, use it for full format with all information
    if (detailedTithiData) {
        // Handle both API formats: new (name, paksha_tithi, completion) and old (tithi_name, paksha_number, completion_percentage)
        const tithiName = detailedTithiData.name || detailedTithiData.tithi_name;
        const pakshaNumber = detailedTithiData.paksha_tithi || detailedTithiData.paksha_number;
        const completion = detailedTithiData.completion !== undefined ? detailedTithiData.completion : detailedTithiData.completion_percentage;
        const remainingPercent = completion !== undefined ? (100 - completion).toFixed(2) : '0.00';

        return `${detailedTithiData.paksha} ${tithiName} (${pakshaNumber}/15) (${rulingPlanet || detailedTithiData.ruling_planet}) (${detailedTithiData.patron_deity}) - ${detailedTithiData.kind} (${remainingPercent}% left)`;
    }

    // Otherwise, parse and enhance basic tithi string while preserving original info
    // Original format: "Dwitiya (Ch) - Bhadra (60.38% left)"
    const match = basicTithi.match(/^(.+?)\s*\((.+?)\)(?:\s*-\s*(.+?))?\s*\((.+?)%\s*left\)/);

    if (match) {
        const tithiName = match[1].trim();
        const rulingPlanet = match[2].trim();
        const kind = match[3]?.trim();
        const completionLeft = parseFloat(match[4]);

        // Try to determine paksha and number from tithi name
        const tithiNames = [
            'Pratipad', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
            'Shashti', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
            'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima', 'Amavasya'
        ];

        const tithiIndex = tithiNames.findIndex(name => name.toLowerCase() === tithiName.toLowerCase());

        if (tithiIndex !== -1) {
            const paksha = tithiIndex <= 14 ? 'Shukla' : 'Krishna';
            const pakshaNumber = tithiIndex <= 14 ? tithiIndex + 1 : tithiIndex - 14;

            if (kind) {
                return `${paksha} ${tithiName} (${pakshaNumber}/15) (${rulingPlanet}) - ${kind} (${completionLeft}% left)`;
            } else {
                return `${paksha} ${tithiName} (${pakshaNumber}/15) (${rulingPlanet}) (${completionLeft}% left)`;
            }
        }
    }

    // If parsing fails, return original string
    return basicTithi;
};

// Helper functions for field creation
export const createBasicField = (label: string, value: string | number, type?: 'default' | 'toggle') => ({
    label,
    value,
    type: type || 'default' as const,
    category: 'basic' as const
});

export const createDetailedField = (label: string, value: string | number, parentSection: string, type?: 'default' | 'toggle' | 'table') => ({
    label,
    value,
    type: type || 'default' as const,
    category: 'detailed' as const,
    parentSection
});

/**
 * Factory function to create divisional chart sections from configuration
 * Preserves all existing functionality while using centralized config
 */
export const createDivisionalSection = (chart: DivisionalChart): AccordionItem => {
    const details: any[] = [
        { label: chart.vedicName, value: '', showValue: false, category: 'basic', type: 'default' }
    ];

    // Add toggle configuration if present (for D7, D8 with universal chakras)
    if (chart.toggleConfig) {
        details.push(createDetailedField(
            chart.toggleConfig.label,
            '',
            chart.vedicName,
            'toggle'
        ));
    } else {
        // Regular placeholder for table components (D12, D40, D60, etc)
        details.push(createDetailedField('', '', chart.vedicName, 'default'));
    }

    return {
        id: chart.id,
        name: chart.name,
        short_name: chart.shortName,
        type: 'divisional',
        details
    };
};

// Helper: parse "HH:MM:SS" to seconds since midnight
const timeToSeconds = (t: string): number => {
    const p = t.split(':').map(Number);
    return (p[0] || 0) * 3600 + (p[1] || 0) * 60 + (p[2] || 0);
};

// Helper: format seconds as "Xh Ym Zs"
const formatDurationHMS = (sec: number): string => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    return `${h}h ${m}m ${s}s`;
};

// Helper function to calculate day length
const calculateDayLength = (astronomical: any): string => {
    if (astronomical?.day_duration_sec) {
        return formatDurationHMS(astronomical.day_duration_sec);
    }
    if (astronomical?.sunrise && astronomical?.sunset) {
        const sec = timeToSeconds(astronomical.sunset) - timeToSeconds(astronomical.sunrise);
        return sec > 0 ? formatDurationHMS(sec) : 'N/A';
    }
    return 'N/A';
};

// Helper function to calculate night length
const calculateNightLength = (astronomical: any): string => {
    if (astronomical?.night_duration_sec) {
        return formatDurationHMS(astronomical.night_duration_sec);
    }
    if (astronomical?.sunrise && astronomical?.sunset) {
        const daySec = timeToSeconds(astronomical.sunset) - timeToSeconds(astronomical.sunrise);
        return daySec > 0 ? formatDurationHMS(86400 - daySec) : 'N/A';
    }
    return 'N/A';
};

// Helper: elapsed time since sunrise
const calculateElapsedSinceSunrise = (astronomical: any, basicInfo: any): string => {
    if (!astronomical?.sunrise || !basicInfo?.time) return 'N/A';
    const sunriseSec = timeToSeconds(astronomical.sunrise);
    const chartSec = timeToSeconds(basicInfo.time);
    let elapsed = chartSec - sunriseSec;
    if (elapsed < 0) elapsed += 86400; // pre-sunrise
    return formatDurationHMS(elapsed);
};

/**
 * Build Solar & Time field configurations
 */
const buildSolarTimeFieldConfigs = (astronomical: any, basicInfo: any, horaDetails: any): FieldConfig[] => [
    {
        key: 'sunrise',
        label: 'Sunrise',
        getValue: (data: any) => data?.sunrise,
        required: true,
        editable: false
    },
    {
        key: 'sunset',
        label: 'Sunset',
        getValue: (data: any) => data?.sunset,
        required: true,
        editable: false
    },
    {
        key: 'next_sunrise',
        label: 'Next Sunrise',
        getValue: (data: any) => data?.next_sunrise,
        required: false,
        editable: false
    },
    {
        key: 'elapsed_since_sunrise',
        label: 'Elapsed Since Sunrise',
        getValue: () => calculateElapsedSinceSunrise(astronomical, basicInfo),
        required: true,
        editable: false
    },
    {
        key: 'day_length',
        label: 'Day Length',
        getValue: () => calculateDayLength(astronomical),
        required: true,
        editable: false
    },
    {
        key: 'night_length',
        label: 'Night Length',
        getValue: () => calculateNightLength(astronomical),
        required: true,
        editable: false
    },
    {
        key: 'hora_current',
        label: 'Current Hora',
        getValue: () => horaDetails
            ? `${horaDetails.current_planet} (#${horaDetails.hour_number}, ${horaDetails.period})`
            : undefined,
        required: false,
        editable: false
    },
    {
        key: 'hora_time',
        label: 'Hora Time',
        getValue: () => horaDetails?.hora_start && horaDetails?.hora_end
            ? `${horaDetails.hora_start} — ${horaDetails.hora_end}`
            : undefined,
        required: false,
        editable: false
    },
    {
        key: 'hora_duration',
        label: 'Hora Duration',
        getValue: () => horaDetails?.hora_duration_sec
            ? formatDurationHMS(horaDetails.hora_duration_sec)
            : undefined,
        required: false,
        editable: false
    },
    {
        key: 'janma_ghatis',
        label: 'Janma Ghatis',
        getValue: (data: any) => data?.janma_ghatis,
        required: true,
        editable: false
    },
    {
        key: 'sidereal_time',
        label: 'Sidereal Time',
        getValue: (data: any) => data?.sidereal_time,
        required: true,
        editable: false
    },
    {
        key: 'ayanamsa',
        label: 'Ayanamsa',
        getValue: (data: any) => data?.ayanamsa,
        required: true,
        editable: false
    },
    {
        key: 'ayanamsa_system',
        label: 'Ayanamsa System',
        getValue: (data: any) => data?.ayanamsa_system,
        required: true,
        editable: false
    },
    {
        key: 'julian_day',
        label: 'Julian Day',
        getValue: (data: any) => data?.julian_day,
        required: true,
        editable: false
    }
];

/**
 * Create Solar & Time Data section from astronomical data
 */
const createSolarTimeSection = (astronomicalData: any, fieldConfigs: FieldConfig[]) => {
    if (!astronomicalData) return null;

    const details = fieldConfigs
        .map(config => {
            const value = config.getValue(astronomicalData);
            // Hide optional fields when value is not available
            if (!config.required && !value) return null;
            return { label: config.label, value: value || 'N/A' };
        })
        .filter(Boolean);

    return {
        id: 'solar-time-data',
        name: 'Solar & Time Data',
        short_name: 'ST',
        type: 'astronomical',
        details
    };
};

/**
 * Map panchanga data to accordion items - EXACT COPY from dc5c830b with plugin integration
 */
export const generatePluginSections = (
    data: { panchanga: any; detailed_panchanga: any; basic_info: any; astronomical: any },
    sharedViewMode?: SharedViewMode,
    sharedChartData?: SharedChartData
) => {
    const {panchanga, detailed_panchanga, basic_info: basicInfo, astronomical} = data;
    const items: AccordionItem[] = [];

    // Basic Panchanga with detailed information integrated into corresponding fields (moved to first position)
    if (panchanga) {
        const details = [];

        // Lunar Year-Month with Samvat and Masa details
        details.push(createBasicField('Lunar Year-Month', panchanga.lunar_year_month));
        if (detailed_panchanga?.samvat_details) {
            const samvat = detailed_panchanga.samvat_details;
            details.push(
                createDetailedField('Samvat Name', samvat.name, 'Lunar Year-Month'),
                createDetailedField('Year Start', samvat.year_start, 'Lunar Year-Month'),
                ...(samvat.jupiter_pos !== undefined ? [
                    createDetailedField('Jupiter Position', `${samvat.jupiter_pos.toFixed(2)}°`, 'Lunar Year-Month')
                ] : []),
                ...(samvat.source ? [
                    createDetailedField('Source', samvat.source, 'Lunar Year-Month')
                ] : [])
            );
        }
        if (detailed_panchanga?.masa_details) {
            const masa = detailed_panchanga.masa_details;
            details.push(
                createDetailedField('Masa Name', masa.name, 'Lunar Year-Month'),
                ...(masa.alternative ? [createDetailedField('Alternative Name', masa.alternative, 'Lunar Year-Month')] : []),
                ...(masa.ruling_deity ? [createDetailedField('Ruling Deity', masa.ruling_deity, 'Lunar Year-Month')] : []),
                ...(masa.season ? [createDetailedField('Season (Ritu)', masa.season, 'Lunar Year-Month')] : []),
                ...(masa.completion !== undefined ? [createDetailedField('Masa Completion', `${masa.completion.toFixed(2)}%`, 'Lunar Year-Month')] : []),
                ...(masa.days_to_next !== undefined ? [createDetailedField('Days to Next', masa.days_to_next.toFixed(2), 'Lunar Year-Month')] : []),
                createDetailedField('Is Adhika', masa.is_adhika ? 'Yes' : 'No', 'Lunar Year-Month')
            );
        }

        // Tithi with detailed info - only show formatted tithi, details will be in expandable section
        details.push(createBasicField('Tithi', formatTithi(panchanga.tithi, detailed_panchanga?.tithi_details)));

        // Add tithi details for expandable section
        if (detailed_panchanga?.tithi_details) {
            const tithi = detailed_panchanga.tithi_details;
            details.push(
                createDetailedField('Tithi Number', tithi.number, 'Tithi'),
                createDetailedField('Paksha Number', `${tithi.paksha_tithi}/15`, 'Tithi'),
                createDetailedField('Ruling Planet', tithi.ruling_planet, 'Tithi'),
                createDetailedField('Patron Deity', tithi.patron_deity, 'Tithi'),
                createDetailedField('Kind', tithi.kind, 'Tithi'),
                createDetailedField('Tithi Completion', `${tithi.completion.toFixed(2)}%`, 'Tithi'),
                createDetailedField('Remaining', tithi.remaining.toFixed(2), 'Tithi')
            );
        }

        // Vara with detailed info
        details.push(createBasicField('Vara', panchanga.weekday));
        if (detailed_panchanga?.vara_details) {
            const vara = detailed_panchanga.vara_details;
            details.push(
                createDetailedField('Vara Number', vara.number, 'Vara'),
                createDetailedField('Sanskrit', vara.sanskrit, 'Vara'),
                createDetailedField('English', vara.english, 'Vara'),
                createDetailedField('Ruling Planet', vara.ruling_planet, 'Vara'),
                ...(vara.planet_code ? [createDetailedField('Planet Code', vara.planet_code, 'Vara')] : [])
            );
        }

        // Nakshatra with detailed info
        details.push(createBasicField('Nakshatra', panchanga.nakshatra));

        // Nakshatra Chakra toggle - first field in Nakshatra details
        // UniversalDivisionalToggle will automatically render Pada toggle inside
        details.push(createDetailedField('Nakshatra Chakra', '', 'Nakshatra', 'toggle'));

        if (detailed_panchanga?.nakshatra_details) {
            const nakshatra = detailed_panchanga.nakshatra_details;
            details.push(
                createDetailedField('Number', nakshatra.number, 'Nakshatra'),
                createDetailedField('Name', nakshatra.name, 'Nakshatra'),
                createDetailedField('Ruling Planet', nakshatra.ruling_planet, 'Nakshatra'),
                createDetailedField('Completion', `${nakshatra.completion.toFixed(2)}%`, 'Nakshatra'),
                createDetailedField('Remaining', nakshatra.remaining.toFixed(2), 'Nakshatra'),
                ...(nakshatra.remaining_arc_minutes ? [createDetailedField('Arc Minutes Left', `${nakshatra.remaining_arc_minutes.toFixed(2)}'`, 'Nakshatra')] : [])
            );
        }

        // Add Nakshatra Anga section
        details.push(createDetailedField('Nakshatra Anga', '', 'Nakshatra', 'table'));

        // Yoga with detailed info
        details.push(createBasicField('Yoga', panchanga.yoga));
        if (detailed_panchanga?.yoga_details) {
            const yoga = detailed_panchanga.yoga_details;
            details.push(
                createDetailedField('Yoga Number', yoga.number, 'Yoga'),
                createDetailedField('Yoga Name', yoga.name, 'Yoga'),
                createDetailedField('Ruling Planet', yoga.ruling_planet, 'Yoga'),
                createDetailedField('Yoga Completion', `${yoga.completion.toFixed(2)}%`, 'Yoga'),
                createDetailedField('Remaining', yoga.remaining.toFixed(2), 'Yoga')
            );
        }

        // Karana with detailed info
        details.push(createBasicField('Karana', panchanga.karana));
        if (detailed_panchanga?.karana_details) {
            const karana = detailed_panchanga.karana_details;
            details.push(
                createDetailedField('Karana Name', karana.name, 'Karana'),
                createDetailedField('Type', karana.type, 'Karana'),
                createDetailedField('Ruling Planet', karana.ruling_planet, 'Karana'),
                ...(karana.completion_percentage !== undefined ? [createDetailedField('Karana Completion', `${karana.completion_percentage.toFixed(2)}%`, 'Karana')] : []),
                ...(karana.remaining_degrees !== undefined ? [createDetailedField('Remaining Degrees', `${karana.remaining_degrees.toFixed(2)}°`, 'Karana')] : [])
            );
        }

        // Hora Lord with detailed info
        details.push(createBasicField('Hora Lord', panchanga.hora_lord));
        if (detailed_panchanga?.hora_details) {
            const hora = detailed_panchanga.hora_details;
            details.push(
                createDetailedField('Hour Number', hora.hour_number, 'Hora Lord'),
                createDetailedField('Current Planet', hora.current_planet, 'Hora Lord'),
                ...(hora.period ? [createDetailedField('Period', hora.period, 'Hora Lord')] : [])
            );
        }

        // Muhurta with detailed info
        if (panchanga.muhurta) {
            details.push(createBasicField('Muhurta', panchanga.muhurta + (detailed_panchanga?.muhurta_details?.graha_ruler ? ' (' + detailed_panchanga.muhurta_details.graha_ruler + ')' : '')));

            // Muhurta Chakra toggle - first field in Muhurta details
            details.push(createDetailedField('Muhurta Chakra', '', 'Muhurta', 'toggle'));

            if (detailed_panchanga?.muhurta_details) {
                const muhurta = detailed_panchanga.muhurta_details;
                details.push(
                    createDetailedField('Muhurta Number', muhurta.number, 'Muhurta'),
                    createDetailedField('Muhurta Name', muhurta.name, 'Muhurta'),
                    ...(muhurta.graha_ruler ? [createDetailedField('Muhurta Ruler', muhurta.graha_ruler, 'Muhurta')] : []),
                    createDetailedField('Lord/Deity', muhurta.lord, 'Muhurta'),
                    createDetailedField('Muhurta Completion', `${muhurta.completion.toFixed(2)}%`, 'Muhurta'),
                    createDetailedField('Elapsed', muhurta.elapsed.toFixed(2), 'Muhurta'),
                    ...(muhurta.current_ghati ? [createDetailedField('Current Ghati', muhurta.current_ghati, 'Muhurta')] : [])
                );
            }
        } else {
            // If no basic muhurta data, still show section with toggle
            details.push(createBasicField('Muhurta', 'No data available'));
            details.push(createDetailedField('Muhurta Chakra', '', 'Muhurta', 'toggle'));
        }

        // Ghati with detailed info
        if (panchanga.ghati) {
            details.push(createBasicField('Ghati', panchanga.ghati));
            if (detailed_panchanga?.ghati_details) {
                const ghati = detailed_panchanga.ghati_details;
                details.push(
                    createDetailedField('Ghati Number', ghati.number, 'Ghati'),
                    createDetailedField('Ghati Name', ghati.name, 'Ghati'),
                    createDetailedField('Lord/Deity', ghati.lord, 'Ghati'),
                    createDetailedField('Parent Muhurta', ghati.parent_muhurta, 'Ghati'),
                    ...(ghati.janma_ghati ? [createDetailedField('Janma Ghati', ghati.janma_ghati, 'Ghati')] : []),
                    ...(ghati.traditional ? [createDetailedField('Traditional', ghati.traditional, 'Ghati')] : [])
                );
            }
        }

        // Store panchanga details in metadata for tab switching
        items.push({
            id: 'basic-panchanga',
            name: 'Basic Panchanga',
            short_name: 'BP',
            type: 'panchanga-combined', // New type for combined section
            details,
            metadata: { panchangaDetails: details }
        });
    }

    // Solar & Time Data section
    const horaDetails = detailed_panchanga?.hora_details;
    const solarTimeFieldConfigs = buildSolarTimeFieldConfigs(astronomical, basicInfo, horaDetails);
    const solarTimeSection = createSolarTimeSection(astronomical, solarTimeFieldConfigs);

    if (solarTimeSection && items.length > 0 && items[0].type === 'panchanga-combined') {
        items[0].metadata = {
            ...items[0].metadata,
            solarTimeDetails: solarTimeSection.details
        };
    }

    // Divisional Charts - combined into single section with tab switcher
    // Store all divisional sections in metadata for tab switching
    const divisionalSections: Record<string, AccordionItem> = {};
    DIVISIONAL_CHARTS.forEach(chart => {
        divisionalSections[chart.shortName.toLowerCase()] = createDivisionalSection(chart);
    });

    items.push({
        id: 'divisional-charts',
        name: 'Divisional Charts',
        short_name: 'DC',
        type: 'divisional-combined',
        details: [],
        metadata: { divisionalSections }
    });

    return items;
};
